import { useEffect, useMemo, useState } from "react";

import { MDXProvider } from "@mdx-js/react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { type Page, type SidebarGroup, type SidebarNode, type TocEntry } from "../../types/content.ts";
import DocsLayout from "../layout/DocsLayout.jsx";
import { firstPageOf, loadPage, resolvePage, sectionConfig, sidebarForSection } from "../lib/content.js";
import { useLocale } from "../lib/i18n.jsx";
import { useHashScroll, useScrollSpy } from "../lib/useScrollSpy.js";
import components from "../theme/MDXComponents.jsx";
import { ProseSkeleton } from "../theme/Skeleton.jsx";

export default function GuidePage() {
  const { locale, section, slug } = useParams();
  const { hash } = useLocation();
  const { t } = useLocale();
  const [module, setModule] = useState<MdxModule | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const config = sectionConfig(section);
  const { page, untranslated } = resolvePage(locale ?? "", section ?? "", slug ?? null);

  useEffect(() => {
    if (!page) return undefined;
    let alive = true;
    setModule(null);
    setError(null);
    loadPage(page)
      .then((m) => alive && setModule(m))
      .catch((e: Error) => alive && setError(e));
    return () => {
      alive = false;
    };
  }, [page?.file]);

  const toc: TocEntry[] = module?.toc ?? [];
  // Só H2 na sidebar: com H3 junto o índice fica mais alto que a própria lista
  // de páginas e a navegação some abaixo da dobra.
  const headings = useMemo(() => toc.filter((h) => h.depth === 2), [toc]);
  const activeAnchor = useScrollSpy(headings.map((h) => h.id));

  // Só depois que o MDX montou é que a âncora do link existe no DOM.
  useHashScroll(hash, !!module);

  const sidebar = useMemo(() => {
    if (!config || config.type !== "guides") return [];
    // A árvore vem de content.js; aqui ela só ganha rota, estado ativo e o
    // índice da página aberta. O mapeamento é recursivo porque um nó pode ser um
    // grupo-pai (`groups`) em vez de um grupo de páginas (`items`).
    const toNode = (group: SidebarGroup): SidebarNode => ({
      id: group.id,
      label: group.label,
      collapsed: group.collapsed,
      items: (group.items || []).map((p: Page) => ({
        key: p.file,
        label: p.title,
        to: `/${locale}/${section}/${p.slug}`,
        active: p.slug === slug,
        children:
          p.slug === slug ? headings.map((h) => ({ id: h.id, label: h.text, active: activeAnchor === h.id })) : null,
      })),
      groups: (group.groups || []).map(toNode),
    });

    return sidebarForSection(locale ?? "", section ?? "").map(toNode);
  }, [config, locale, section, slug, headings, activeAnchor]);

  // /:locale/:section sem slug numa seção de guias → primeira página.
  if (config?.type === "guides" && !slug) {
    const first = firstPageOf(locale ?? "", section ?? "");
    if (first) return <Navigate to={`/${locale}/${section}/${first.slug}`} replace />;
  }

  const body = () => {
    if (!page) return <p className="doc-p">{t("notFound")}</p>;
    if (error)
      return (
        <pre className="doc-code">
          <code>{String(error.message || error)}</code>
        </pre>
      );
    if (!module) return <ProseSkeleton />;

    const Content = module.default;
    return (
      <>
        {untranslated && <div className="docs-untranslated">{t("untranslated")}</div>}
        <MDXProvider components={components}>
          <Content />
        </MDXProvider>
      </>
    );
  };

  return (
    <DocsLayout activeSection={section} sidebar={sidebar} contentKey={`${locale}/${section}/${slug || ""}`}>
      {body()}
    </DocsLayout>
  );
}
